// src/components/authorizations/authorizationMeta.ts

import type {
  AuthorizationType,
  AuthorizationDTO,
} from "@/types/authorizations";

export function normalizeEnumValue(v: any): string {
  if (!v) return "";
  const s = String(v);
  const parts = s.split(".");
  return parts[parts.length - 1];
}

export function normalizeAuthorizationType(v: any): AuthorizationType | null {
  const t = normalizeEnumValue(v).toUpperCase();
  if (t === "SQL") return "SQL";
  if (t === "BIGQUERY") return "BIGQUERY";
  if (t === "BREVO") return "BREVO";
  if (t === "ACTIVECAMPAIGN") return "ACTIVECAMPAIGN";
  if (t === "FACEBOOK") return "FACEBOOK";
  if (t === "GOOGLE") return "GOOGLE";
  if (t === "GITHUB") return "GITHUB";
  if (t === "SLACK") return "SLACK";
  if (t === "CLOUDFLARE_KV") return "CLOUDFLARE_KV";
  if (t === "HTTP_KV") return "HTTP_KV";
  if (t === "SHOPIFY") return "SHOPIFY";
  return null;
}

/**
 * Maps a normalized auth type to the string key understood by
 * ConnectorIcon / DataSourceIcon. Returns null for types that should
 * render via Tabler icons (SQL, GOOGLE, GITHUB) or the fallback.
 */
export function authorizationConnectorIconKey(v: any): string | null {
  const t = normalizeAuthorizationType(v);
  if (t === "BIGQUERY") return "BIGQUERY";
  if (t === "BREVO") return "BREVO";
  if (t === "ACTIVECAMPAIGN") return "ACTIVECAMPAIGN";
  if (t === "FACEBOOK") return "FACEBOOK";
  if (t === "SHOPIFY") return "SHOPIFY";
  return null;
}

export const AUTHORIZATION_META: Record<
  AuthorizationType,
  {
    label: string;
    shortLabel?: string;
    description: string;
  }
> = {
  SQL: {
    label: "SQL",
    shortLabel: "SQL",
    description: "Reusable host/db + credentials profile for SQL databases.",
  },
  BIGQUERY: {
    label: "BigQuery",
    shortLabel: "BigQuery",
    description: "Reusable Google service account profile.",
  },
  BREVO: {
    label: "Brevo",
    shortLabel: "Brevo",
    description: "Brevo ESP API key for email contact list syncing.",
  },
  FACEBOOK: {
    label: "Facebook",
    shortLabel: "Facebook",
    description: "Facebook / Meta OAuth credentials.",
  },
  GOOGLE: {
    label: "Google",
    shortLabel: "Google",
    description: "Google OAuth credentials.",
  },
  GITHUB: {
    label: "GitHub",
    shortLabel: "GitHub",
    description: "GitHub OAuth / PAT credentials.",
  },
  SLACK: {
    label: "Slack",
    shortLabel: "Slack",
    description:
      "Account-level Slack bot-token. Managed from Settings → Integrations.",
  },
  CLOUDFLARE_KV: {
    label: "Cloudflare KV",
    shortLabel: "Cloudflare",
    description:
      "Cloudflare API token + namespace for syncing visitor segments to Cloudflare Workers KV.",
  },
  HTTP_KV: {
    label: "Custom KV endpoint",
    shortLabel: "Custom KV",
    description:
      "A customer-hosted HTTP endpoint + API key for syncing visitor segments to your own key-value store.",
  },
  SHOPIFY: {
    label: "Shopify",
    shortLabel: "Shopify",
    description: "Shopify store connection (OAuth) for syncing orders, customers and products.",
  },
  MICROSOFT_ADS: {
    label: "Microsoft Ads",
    shortLabel: "MS Ads",
    description: "Microsoft Advertising connection for syncing campaign performance.",
  },
  KLAVIYO: {
    label: "Klaviyo",
    shortLabel: "Klaviyo",
    description: "Klaviyo API key connection for syncing email events, campaigns and profiles.",
  },
  WOOCOMMERCE: {
    label: "WooCommerce",
    shortLabel: "WooCommerce",
    description: "WooCommerce store address + read-only REST API key for syncing orders, refunds and products.",
  },
  ACTIVECAMPAIGN: {
    label: "ActiveCampaign",
    shortLabel: "ActiveCampaign",
    description: "ActiveCampaign API URL + API key for syncing campaigns, email engagement and contacts.",
  },
  SNOWFLAKE: {
    label: "Snowflake",
    shortLabel: "Snowflake",
    description:
      "Snowflake key-pair credentials for querying your own warehouse.",
  },
  MOLLIE: {
    label: "Mollie",
    shortLabel: "Mollie",
    description: "Read-only Mollie access token for importing payments, fees and settlements.",
  },
  MAGENTO: {
    label: "Magento",
    shortLabel: "Magento",
    description: "Magento connection.",
  },
};

export function normalizeAuthorization(a: any): AuthorizationDTO {
  const id = String(a?.id ?? a?.public_id ?? "");
  const typeNorm = normalizeAuthorizationType(a?.type) ?? String(a?.type ?? "");

  return {
    id,
    name: String(a?.name ?? ""),
    type: typeNorm,
    config: a?.config && typeof a.config === "object" ? a.config : {},

    created_at: a?.created_at ?? null,
    updated_at: a?.updated_at ?? null,
    created_by_user_id: a?.created_by_user_id ?? null,
    linked_sources_count: Number(a?.linked_sources_count ?? 0),

    // Ownership (ADR-0329): passed through only when present, so an absent
    // field keeps meaning "shared, usable" (see authorizationOwnership.tsx).
    ...(typeof a?.is_personal === "boolean" ? { is_personal: a.is_personal } : {}),
    ...(typeof a?.usable_by_me === "boolean" ? { usable_by_me: a.usable_by_me } : {}),
    ...(a?.created_by_name !== undefined ? { created_by_name: a.created_by_name ?? null } : {}),
    ...(typeof a?.owner_is_member === "boolean" ? { owner_is_member: a.owner_is_member } : {}),

    // Optional convenience: if you want display labels directly on DTO,
    // you can compute them in the page instead of storing here.
  };
}

export function authorizationTypeLabel(type: any): string {
  const key = normalizeAuthorizationType(type);
  if (!key) return normalizeEnumValue(type) || String(type ?? "");
  return AUTHORIZATION_META[key].shortLabel ?? AUTHORIZATION_META[key].label;
}
